import { useState } from "react";
import { Link } from "react-router-dom";
import { Carte } from "@/composants/ui/carte";
import { Champ } from "@/composants/ui/champ";
import { Chargement, Erreur, Vide } from "@/composants/ui/etats";
import { Etiquette } from "@/composants/ui/etiquette";
import { PiedDeListe } from "@/composants/ui/pied-de-liste";
import { useListePaginee } from "@/lib/chargement";
import { typeClient } from "@/lib/libelles";
import { api, type Client } from "@/lib/api";

/**
 * Le carnet d'adresses — restreint au périmètre, comme tout le reste.
 *
 * Un client n'y est visible que si l'un de ses lots l'est : un poseur
 * affecté à un seul lot ne lit pas les coordonnées des autres clients.
 * La règle est dans la requête SQL, jamais dans cet écran.
 */
export function PageClients() {
  const [terme, setTerme] = useState("");
  const [cherche, setCherche] = useState("");
  const liste = useListePaginee<Client>(
    (decalage, signal) => api.clients({ q: cherche || undefined, decalage }, signal),
    [cherche],
  );

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-xl font-semibold text-encre">Clients</h1>
        <p className="mt-1 text-sm text-encre-douce">
          Un client naît avec sa première Commande / Projet ; il ne se crée pas seul.
        </p>
      </div>

      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(evenement) => {
          evenement.preventDefault();
          setCherche(terme.trim());
        }}
      >
        <Champ
          libelle="Filtrer"
          className="min-w-[14rem] flex-1"
          placeholder="Nom, prénom, société, ville…"
          value={terme}
          onChange={(evenement) => setTerme(evenement.target.value)}
        />
      </form>

      {liste.erreur ? <Erreur message={liste.erreur} onReessayer={liste.recharger} /> : null}
      {!liste.erreur && liste.enCours ? <Chargement message="Chargement des clients…" /> : null}

      {!liste.enCours && liste.total === 0 ? (
        <Carte>
          <Vide
            titre="Aucun client"
            detail={
              cherche
                ? "Aucun client de votre périmètre ne correspond à cette recherche."
                : "Les clients apparaissent ici avec leur première Commande / Projet."
            }
          />
        </Carte>
      ) : null}

      {liste.elements.length > 0 ? (
        <Carte>
          <ul className="divide-y divide-filet">
            {liste.elements.map((client) => (
              <li key={client.id}>
                <Link
                  to={`/clients/${client.id}`}
                  className="flex flex-col gap-1 px-4 py-3 hover:bg-chene-clair/25 sm:flex-row sm:items-center sm:gap-4"
                >
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-encre">
                    {client.designation}
                  </span>
                  <span className="text-sm text-encre-douce sm:w-40 sm:shrink-0">
                    {client.ville ?? "—"}
                  </span>
                  <span className="text-sm text-encre-douce sm:w-28 sm:shrink-0">
                    {client.nombre_commandes} commande{client.nombre_commandes > 1 ? "s" : ""}
                  </span>
                  <span className="sm:w-32 sm:shrink-0">
                    <Etiquette>{typeClient(client.type)}</Etiquette>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          <PiedDeListe
            nom="client"
            affiches={liste.elements.length}
            total={liste.total}
            reste={liste.reste}
            enCours={liste.chargeLaSuite}
            onCharger={liste.chargerLaSuite}
          />
        </Carte>
      ) : null}
    </div>
  );
}
